import { onMounted, onUnmounted, ref, watch } from "@vue/composition-api";
import { throttle } from "lodash-es";
import { InputNotificationSearch, Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { EVENTS } from "@/const";

export default function useNotifications() {
  const { authUser } = useAuthUser();

  const unreadNotificationsCount = ref(0);
  const loading = ref(false);

  const { enableScroll, getNextPage: getNextData, getFirstPage: getNotifications, items: notifications } = usePager(
    (page, requestData?: InputNotificationSearch) => {
      return providers.NotificationsProvider.find({
        data: {
          recipientId: authUser.value?.id,
          page,
          sort: "TIME,DESC",
          ...requestData,
        },
      });
    }
  );

  const getUnreadNotificationsCount = async () => {
    const unreadNotificationsStats = await providers.NotificationsProvider.getUnreadStats();
    if (unreadNotificationsStats) unreadNotificationsCount.value = unreadNotificationsStats.count;
  };

  const receivedNotification = throttle(getUnreadNotificationsCount, 3000, { trailing: false });

  const markRead = async (notification: Notification) => {
    if (notification.id === "0") return;
    const response = await providers.NotificationsProvider.markRead({ notificationId: notification.id });
    if (response) emitter.emit<any>(EVENTS.NOTIFICATIONS_MARKED_READ);
  };

  watch(
    authUser,
    async () => {
      if (authUser.value) await getUnreadNotificationsCount();
    },
    { immediate: true }
  );

  onMounted(async () => {
    emitter.on<any>(EVENTS.NOTIFICATIONS_NEW_NOTIFICATION, receivedNotification);
    emitter.on<any>(EVENTS.NOTIFICATIONS_MARKED_READ, getUnreadNotificationsCount);
  });

  onUnmounted(() => {
    emitter.off<any>(EVENTS.NOTIFICATIONS_NEW_NOTIFICATION, receivedNotification);
    emitter.off<any>(EVENTS.NOTIFICATIONS_MARKED_READ, getUnreadNotificationsCount);
  });

  return { loading, enableScroll, notifications, getNotifications, getNextData, markRead, unreadNotificationsCount };
}
